import { badNotification } from '@/components/Notification/Notification';
import type { SetFieldError } from '@mantine/form/lib/types';
import type { Dispatch, SetStateAction } from 'react';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';

/**
 * A function to handle BE error.
 * It will display the main error message as bad notification.
 * For handling field errors see displayErrorOnMantineForm, displayErrorOnReactHookForm or displayErrorOnUseState.
 * @param error
 * @param setFieldError - field error setter from react-hook-form
 */
export function displayError(error: any): boolean {
  // no response and no request
  if (!error.response && !error.request) {
    badNotification('Špatný požadavek!', 'Zkuste znovu');
    console.error('Client error: ', error);
    return false;
  }

  // no response
  if (!error.response) {
    badNotification('Server neodpovídá!', 'Zkuste to znovu později');
    console.error('Server error: ', error);
    return false;
  }

  const message =
    'Nelze rozhodnout, co je špatně... Zkuste znovu a nebo obraťte se na IT podporu';

  // nginx returns 413 and html in data
  if (error.response.status === 413) {
    badNotification(
      'Příloha je moc velká!',
      'Vybraná příloha je příliš velká a nelze ji uložit na server!'
    );
    console.error('Error 413: ', error.response);
    return false;
  }

  // unexpected response fromat
  if (!error.response.data) {
    badNotification('Vznikla chyba!', message);
    console.error('Unusual error format: ', error.response);
    return false;
  }

  // when no code, either say unexpected error or provide message
  if (!error.response.data.status) {
    badNotification(
      'Vznikla chyba!',
      !error.response.data.message ? message : error.response.data.message
    );
    console.error('Error with no status code: ', error.response);
    return false;
  }

  if (error.response.data.status === 401) {
    badNotification('Vznikla chyba!', 'Nespravné přihlašovací údaje!');
    console.error('Error 401: ', error.response);
    return false;
  }

  if (error.response.data.status === 403) {
    badNotification(
      'Vznikla chyba!',
      'Nemáte na to dostatečné opravnění nebo Váše přihlášení vypršelo!'
    );
    console.error('Error 403: ', error.response);
    return false;
  }

  // when no message, provide status code + error if it is provided
  if (!error.response.data.message) {
    badNotification(
      'Vznikla chyba!',
      'Status chyby: ' + error.response.data.status + error.response.data.error
        ? ' ' + error.response.data.error
        : ''
    );
    console.error('Error with no message: ', error.response);
    return false;
  }

  badNotification('Vznikla chyba!', error.response.data.message);
  return true;
}

// We expect the second function from [val, setVal] = useState<string> clause
export type SetStateFunction<T> = Dispatch<SetStateAction<T>>;

export interface FieldErrorState {
  field: string;
  setState: SetStateFunction<string>;
}

/**
 * A function to handle any BE error.
 * It will display the main error message and errors for each error field if so were provided.
 * Be careful with naming! Form fields must have the same names as in BE response.
 * It is designed to handle field errors using use state.
 * For mantine/form see displayErrorOnMantineForm.
 * For react-hook-form see displayErrorOnReactHookForm.
 * @param error
 * @param fieldErrorStates - pairs of field name and a function to set error for that field
 */
export function displayErrorOnUseState(
  error: any,
  fieldErrorStates: FieldErrorState[]
): void {
  if (!displayError(error)) return;

  const fieldErrors = error.response.data.errors;
  if (!fieldErrors) return;

  // Match error message from BE with setState function for each field
  for (const fieldError of fieldErrors) {
    const fieldErrorState = fieldErrorStates.find(
      el => el.field === fieldError.field
    );

    fieldErrorState?.setState(fieldError.message);
  }
}

/**
 * A function to handle any BE error.
 * It will display the main error message and errors for each error field if so were provided.
 * Be careful with naming! Form fields must have the same names as in BE response.
 * It is designed to handle field errors using react-hook-form.
 * For mantine/form see displayErrorOnMantineForm.
 * For useState see displayErrorOnUseState.
 * @param error
 * @param setFieldError - field error setter from react-hook-form
 */
export function displayErrorOnReactHookForm<T extends FieldValues>(
  error: any,
  setFieldError: UseFormSetError<T>
): void {
  if (!displayError(error)) return;

  const fieldErrors = error.response.data.errors;
  if (!fieldErrors) return;

  fieldErrors.forEach((fieldError: { field: Path<T>; message: string }) => {
    setFieldError(fieldError.field, {
      type: 'manual',
      message: fieldError.message,
    });
  });
}

/**
 * A function to handle any BE error.
 * It will display the main error message and errors for each error field if so were provided.
 * Be careful with naming! Form fields must have the same names as in BE response.
 * It is designed to handle field errors using mantine/form.
 * For react-hook-form see displayErrorOnReactHookForm.
 * For useState see displayErrorOnUseState.
 * @param error
 * @param setFieldError - field error setter from mantine/form
 */
export function displayErrorOnMantineForm<T>(
  error: any,
  setFieldError: SetFieldError<T>
): void {
  if (!displayError(error)) return;

  const fieldErrors = error.response.data.errors;
  if (!fieldErrors) return;

  fieldErrors.forEach((fieldError: { field: Path<T>; message: string }) => {
    setFieldError(fieldError.field, fieldError.message);
  });
}

export {};
